import {
  DealerJackProtocol,
  BlackjackProtocolData,
  DealerJackPlayer,
  DealerJackGameSettings,
  DealerJackGameMode,
  Card,
  RoundResult,
} from "./DealerJackProtocol";
import UserSession from "network/UserSession";

export enum DealerJackGamePhase {
  WAITING = "waiting",
  DEALING = "dealing", // 카드 배분 중
  BETTING = "betting", // 카드 확인 후 베팅 단계 (새 위치)
  PLAYER_TURN = "player_turn",
  DEALER_TURN = "dealer_turn",
  RESULTS = "results",
  GAME_END = "game_end",
}

// 하위 호환성을 위한 alias
export const BlackjackGamePhase = DealerJackGamePhase;

export interface DealerJackState {
  players: DealerJackPlayer[];
  dealerCards: Card[];
  dealerValue: number;
  dealerVisibleValue: number; // 딜러의 공개된 카드 값
  currentRound: number;
  totalRounds: number;
  phase: DealerJackGamePhase;
  // currentPlayerId 제거 (동시 액션 모드)
  timeLeft: number;
  gameSettings: DealerJackGameSettings;
  roundResults?: RoundResult[];
  gameEnded: boolean;
  myBet: number;
  chatEnabled: boolean;
  hostId?: number;
  isHost: boolean;
  // UI 관련 상태
  showResults: boolean;
  showFinalScores: boolean;
  finalRankings: { playerId: number; rank: number; chips: number }[];
  nextRoundIn?: number; // 다음 라운드까지 남은 시간 (초)
}

export const initialDealerJackState: DealerJackState = {
  players: [],
  dealerCards: [],
  dealerValue: 0,
  dealerVisibleValue: 0,
  currentRound: 0,
  totalRounds: 5,
  phase: DealerJackGamePhase.WAITING,
  timeLeft: 0,
  gameSettings: {
    gameMode: DealerJackGameMode.DEALER,
    totalRounds: 5,
    startingChips: 50, // 20-100, 10 단위
    bettingTime: 30,
    turnTime: 20,
    minBet: 5, // 기본 최소 베팅액
  },
  gameEnded: false,
  myBet: 0,
  chatEnabled: true,
  isHost: false,
  showResults: false,
  showFinalScores: false,
  finalRankings: [],
};

export type DealerJackAction =
  | { type: "SET_GAME_SETTINGS"; settings: DealerJackGameSettings }
  | { type: "TOGGLE_CHAT" }
  | { type: "SET_MY_BET"; amount: number }
  | { type: "DECREMENT_TIME" }
  | { type: "SHOW_RESULTS"; show: boolean }
  | { type: "SHOW_FINAL_SCORES"; show: boolean }
  | { type: "SET_HOST"; hostId: number; isHost: boolean }
  | { type: "SKIP_RESULTS" }
  | {
      type: "SOCKET_GAME_START";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.GAME_START];
    }
  | {
      type: "SOCKET_ROUND_START";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.ROUND_START];
    }
  | {
      type: "SOCKET_BET_PLACED";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.BET_PLACED];
    }
  | {
      type: "SOCKET_BETTING_TIME_END";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.BETTING_TIME_END];
    }
  | {
      type: "SOCKET_CARDS_DEALT";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.CARDS_DEALT];
    }
  | {
      type: "SOCKET_PLAYER_TURN";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.PLAYER_TURN];
    }
  | {
      type: "SOCKET_CARD_HIT";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.CARD_HIT];
    }
  | {
      type: "SOCKET_PLAYER_STAND";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.PLAYER_STAND];
    }
  | {
      type: "SOCKET_PLAYER_ACTION";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.PLAYER_ACTION];
    }
  | {
      type: "SOCKET_PLAYER_ACTION_COMPLETED";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.PLAYER_ACTION_COMPLETED];
    }
  | {
      type: "SOCKET_DEALER_TURN";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.DEALER_TURN];
    }
  | {
      type: "SOCKET_ROUND_RESULTS";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.ROUND_RESULTS];
    }
  | {
      type: "SOCKET_GAME_END";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.GAME_END];
    }
  | {
      type: "SOCKET_PLAYER_ELIMINATED";
      payload: BlackjackProtocolData[typeof DealerJackProtocol.PLAYER_ELIMINATED];
    };

export function dealerJackReducer(
  state: DealerJackState,
  action: DealerJackAction
): DealerJackState {
  switch (action.type) {
    case "SET_GAME_SETTINGS":
      return {
        ...state,
        gameSettings: action.settings,
        totalRounds: action.settings.totalRounds,
      };

    case "TOGGLE_CHAT":
      return {
        ...state,
        chatEnabled: !state.chatEnabled,
      };

    case "SET_MY_BET":
      return {
        ...state,
        myBet: action.amount,
      };

    case "DECREMENT_TIME":
      return {
        ...state,
        timeLeft: Math.max(0, state.timeLeft - 0.1),
      };

    case "SHOW_RESULTS":
      return {
        ...state,
        showResults: action.show,
      };

    case "SHOW_FINAL_SCORES":
      return {
        ...state,
        showFinalScores: action.show,
      };

    case "SET_HOST":
      return {
        ...state,
        hostId: action.hostId,
        isHost: action.isHost,
      };

    case "SOCKET_GAME_START":
      const myId = UserSession.getInstance().getUserId();
      console.log("MyID" + myId);
      console.log("Host" + action.payload.hostId);
      return {
        ...state,
        players: action.payload.players || state.players || [],
        gameSettings: action.payload.gameSettings,
        totalRounds: action.payload.gameSettings.totalRounds,
        hostId: action.payload.hostId,
        isHost: action.payload.hostId === myId,
        phase: DealerJackGamePhase.WAITING, // 게임 시작 시 대기 단계로
        gameEnded: false,
      };

    case "SOCKET_ROUND_START":
      const updatedPlayersForRoundStart = (action.payload.players || []).map((player) => {
        const existingPlayer = state.players.find(p => p.id === player.id);
        const isEliminated = existingPlayer?.isEliminated || false;
        
        return {
          ...player,
          cards: [],
          handValue: 0,
          currentBet: 0,
          isStanding: false,
          isBust: false,
          isBlackjack: false,
          isActive: !isEliminated, // 파산된 플레이어는 비활성 상태 유지
          actionCompleted: isEliminated, // 파산된 플레이어는 자동으로 액션 완료 처리
          isEliminated: isEliminated, // 파산 상태 유지
          isAllIn: false, // 새 라운드에서는 올인 상태 초기화
        };
      });
      
      return {
        ...state,
        currentRound: action.payload.round,
        totalRounds: action.payload.totalRounds,
        phase: DealerJackGamePhase.BETTING, // 라운드 시작 시 즉시 베팅 단계로
        timeLeft: action.payload.bettingTime, // 베팅 시간 설정
        myBet: 0,
        showResults: false,
        // 플레이어 상태 초기화 (파산 상태는 유지)
        players: updatedPlayersForRoundStart,
        dealerCards: [],
        dealerValue: 0,
        dealerVisibleValue: 0,
        roundResults: undefined,
      };

    case "SOCKET_BET_PLACED":
      return {
        ...state,
        players: (state.players || []).map((player) =>
          player.id === action.payload.playerId
            ? {
                ...player,
                currentBet: action.payload.amount,
                chips: action.payload.playerChips,
              }
            : player
        ),
      };

    case "SOCKET_BETTING_TIME_END":
      // 서버 데이터와 기존 상태를 병합 (제거 상태 유지)
      const serverPlayers = action.payload.players || [];
      const updatedPlayersForBettingEnd = state.players.map((existingPlayer) => {
        const serverPlayer = serverPlayers.find(sp => sp.id === existingPlayer.id);
        const betInfo = action.payload.allBets?.[existingPlayer.id];
        
        return {
          ...existingPlayer, // 기존 상태 유지
          ...(serverPlayer || {}), // 서버 데이터로 업데이트
          isEliminated: existingPlayer.isEliminated, // 제거 상태는 절대 덮어쓰지 않음
          isActive: existingPlayer.isEliminated ? false : (serverPlayer?.isActive ?? existingPlayer.isActive),
          isAllIn: betInfo?.isAllIn || false,
        };
      });
      
      return {
        ...state,
        phase: DealerJackGamePhase.DEALING, // 베팅 종료 후 딜링 단계로
        players: updatedPlayersForBettingEnd,
        timeLeft: 0,
      };

    case "SOCKET_CARDS_DEALT":
      const updatedPlayersForDealing = (state.players || []).map((player) => {
        const playerCards = action.payload.playerCards?.[player.id] || [];
        const handValue = calculateHandValue(playerCards);
        const isBlackjack = handValue === 21 && playerCards.length === 2;
        
        return {
          ...player,
          cards: playerCards,
          handValue: handValue,
          isBlackjack: isBlackjack,
          // 블랙잭인 플레이어나 파산된 플레이어는 액션 완료 상태로 설정
          actionCompleted: isBlackjack || player.isEliminated,
          isStanding: isBlackjack, // 블랙잭은 자동으로 스탠드 처리
          // 파산된 플레이어는 비활성 상태 유지, 블랙잭인 경우만 비활성화
          isActive: player.isEliminated ? false : !isBlackjack,
        };
      });
      
      return {
        ...state,
        phase: DealerJackGamePhase.PLAYER_TURN, // 카드 배분 후 플레이어 턴으로
        timeLeft: 0,
        players: updatedPlayersForDealing,
        dealerCards: action.payload.dealerCards,
        dealerVisibleValue: action.payload.dealerVisibleValue,
        dealerValue: action.payload.dealerVisibleValue, // 초기에는 공개된 카드 값만
      };

    case "SOCKET_PLAYER_TURN":
      return {
        ...state,
        timeLeft: action.payload.timeLimit,
        // 모든 플레이어의 액션 완료 상태 업데이트 (동시 진행)
        players: (state.players || []).map((player) => {
          const canAct = !player.isStanding && !player.isBust && !player.isBlackjack && !player.isEliminated;
          
          return {
            ...player,
            actionCompleted: !canAct, // 이미 액션이 끝난 플레이어나 파산된 플레이어는 완료 상태로
          };
        }),
      };

    case "SOCKET_CARD_HIT":
      return {
        ...state,
        players: (state.players || []).map((player) =>
          player.id === action.payload.playerId
            ? {
                ...player,
                cards: action.payload.newCards,
                handValue: action.payload.handValue,
                isBust: action.payload.isBust,
                isActive: !action.payload.isBust,
              }
            : player
        ),
      };

    case "SOCKET_PLAYER_STAND":
      return {
        ...state,
        players: (state.players || []).map((player) =>
          player.id === action.payload.playerId
            ? {
                ...player,
                isStanding: true,
                isActive: false,
              }
            : player
        ),
      };

    case "SOCKET_PLAYER_ACTION":
      const currentUserId = UserSession.getInstance().getUserId();
      
      // 본인의 액션에만 응답
      if (action.payload.playerId !== currentUserId) {
        return state;
      }

      return {
        ...state,
        players: (state.players || []).map((player) =>
          player.id === action.payload.playerId
            ? {
                ...player,
                // 카드 업데이트 (hit, double_down 시)
                cards: (action.payload.action === "hit" || action.payload.action === "double_down") && action.payload.newCard
                  ? [...player.cards, action.payload.newCard]
                  : player.cards,
                // 핸드 값 업데이트 (surrender 제외)
                handValue: action.payload.handValue ?? player.handValue,
                // 버스트 상태 업데이트 (hit, double_down 시)
                isBust: action.payload.isBust || false,
                // 스탠드 상태 업데이트 (stand, double_down 시)
                isStanding: (action.payload.action === "stand" || action.payload.action === "double_down") 
                  ? true 
                  : player.isStanding,
                // 활성 상태 업데이트 (모든 종료 액션 시 비활성화)
                isActive: (action.payload.action === "stand" || 
                          action.payload.action === "double_down" ||
                          action.payload.action === "surrender" ||
                          action.payload.isBust) 
                  ? false 
                  : player.isActive,
                // 베팅 금액 업데이트 (double_down 시)
                currentBet: action.payload.currentBet ?? player.currentBet,
                // 칩 수 업데이트 (double_down, surrender 시)
                chips: action.payload.remainingChips ?? player.chips,
                // 더블다운 상태 업데이트
                isDoubledDown: action.payload.action === "double_down",
                // 항복 상태 업데이트
                isSurrendered: action.payload.action === "surrender",
                // 액션 완료 상태 업데이트
                actionCompleted: true,
              }
            : player
        ),
      };

    case "SOCKET_PLAYER_ACTION_COMPLETED":
      return {
        ...state,
        players: (state.players || []).map((player) =>
          player.id === action.payload.playerId
            ? {
                ...player,
                actionCompleted: true,
                // 서버에서 전송된 업데이트된 플레이어 상태 적용
                ...(action.payload.playerState && {
                  handValue: action.payload.playerState.handValue,
                  cards: action.payload.playerState.cards,
                  isBust: action.payload.playerState.isBust,
                  isStanding: action.payload.playerState.isStanding,
                  isBlackjack: action.payload.playerState.isBlackjack,
                  isActive: !action.payload.playerState.isBust && !action.payload.playerState.isStanding,
                }),
              }
            : player
        ),
      };

    case "SOCKET_DEALER_TURN":
      return {
        ...state,
        phase: DealerJackGamePhase.DEALER_TURN,
        dealerCards: action.payload.dealerCards,
        dealerValue: action.payload.dealerValue,
        timeLeft: 0,
        // 모든 플레이어의 최종 액션 상태 업데이트 (서버에서 전송된 경우)
        players: action.payload.finalPlayerStates
          ? (state.players || []).map((player) => {
              const finalState = action.payload.finalPlayerStates?.find(
                (fps) => fps.playerId === player.id
              );
              
              return finalState
                ? {
                    ...player,
                    cards: finalState.cards,
                    handValue: finalState.handValue,
                    isBust: finalState.isBust,
                    isStanding: finalState.isStanding,
                    isBlackjack: finalState.isBlackjack,
                    isActive: finalState.isActive,
                    // 액션 완료 상태로 설정
                    actionCompleted: true,
                  }
                : {
                    ...player,
                    actionCompleted: true,
                  };
            })
          : // finalPlayerStates가 없으면 기존 로직 유지
            (state.players || []).map((player) => ({
              ...player,
              actionCompleted: true,
            })),
      };

    case "SOCKET_ROUND_RESULTS":
      return {
        ...state,
        phase: DealerJackGamePhase.RESULTS,
        roundResults: action.payload.results,
        dealerCards: action.payload.dealerFinalCards,
        dealerValue: action.payload.dealerFinalValue,
        showResults: true,
        timeLeft: action.payload.nextRoundIn || 0,
        nextRoundIn: action.payload.nextRoundIn,
        players: (state.players || []).map((player) => {
          const result = action.payload.results?.find(
            (r) => r.playerId === player.id
          );
          return result ? { ...player, chips: result.finalChips } : player;
        }),
      };

    case "SOCKET_GAME_END":
      const rankings = action.payload.finalRankings || action.payload.rankings || [];
      const finalScores = action.payload.finalScores || rankings;
      
      return {
        ...state,
        phase: DealerJackGamePhase.GAME_END,
        gameEnded: true,
        showFinalScores: true,
        finalRankings: rankings,
        players: (state.players || []).map((player) => {
          // finalRankings 또는 finalScores에서 플레이어 정보 찾기
          const finalRanking = rankings.find((r) => r.playerId === player.id);
          const finalScore = finalScores.find((s) => s.playerId === player.id);
          
          const updatedChips = finalRanking?.chips || finalScore?.chips || player.chips;
          
          return {
            ...player,
            chips: updatedChips,
          };
        }),
        totalRounds: action.payload.totalRounds || state.totalRounds,
        gameSettings: action.payload.gameSettings || state.gameSettings,
      };

    case "SOCKET_PLAYER_ELIMINATED":
      return {
        ...state,
        players: state.players.map((player) => {
          const isEliminated = action.payload.eliminatedPlayers.some(
            (ep) => ep.playerId === player.id
          );
          return isEliminated
            ? { ...player, isEliminated: true, isActive: false, chips: 0 }
            : player;
        }),
      };

    case "SKIP_RESULTS":
      return {
        ...state,
        showResults: false,
      };

    default:
      return state;
  }
}

// 헬퍼 함수들

// 핸드 값 계산 함수
export function calculateHandValue(cards: Card[]): number {
  if (!cards || !Array.isArray(cards)) {
    return 0;
  }
  
  let value = 0;
  let aces = 0;

  for (const card of cards) {
    if (card.rank === "A") {
      aces++;
      value += 11;
    } else if (["J", "Q", "K"].includes(card.rank)) {
      value += 10;
    } else {
      value += parseInt(card.rank);
    }
  }

  // A를 1로 계산해야 하는 경우
  while (value > 21 && aces > 0) {
    value -= 10;
    aces--;
  }

  return value;
}

// 블랙잭인지 확인
export function isBlackjack(cards: Card[]): boolean {
  return cards.length === 2 && calculateHandValue(cards) === 21;
}

// 버스트인지 확인
export function isBust(cards: Card[]): boolean {
  return calculateHandValue(cards) > 21;
}

// 카드 표시용 헬퍼 함수
export function getCardDisplay(card: Card): string {
  const suitSymbols = {
    hearts: "♥",
    diamonds: "♦",
    clubs: "♣",
    spades: "♠",
  };
  return `${card.rank}${suitSymbols[card.suit]}`;
}

// 카드 색상 가져오기
export function getCardColor(card: Card): "red" | "black" {
  return card.suit === "hearts" || card.suit === "diamonds" ? "red" : "black";
}

// 하위 호환성을 위한 aliases
export const initialBlackjackState = initialDealerJackState;
export const blackjackReducer = dealerJackReducer;
